import React, { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, deleteUser, resetUserPassword, CrmUser } from '../api/users';
import { useAuth } from '../hooks/useAuth';

const ACCENT = '#C8402A';

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users,      setUsers]      = useState<CrmUser[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [showAdd,    setShowAdd]    = useState(false);
  const [editTarget, setEditTarget] = useState<CrmUser | null>(null);
  const [resetTarget,setResetTarget]= useState<CrmUser | null>(null);
  const [error,      setError]      = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try { setUsers(await getUsers()); }
    finally { setLoading(false); }
  }

  const thStyle: React.CSSProperties = {
    padding: '10px 14px', textAlign: 'left',
    fontSize: 12, fontWeight: 600, color: '#666',
    textTransform: 'uppercase', letterSpacing: '0.04em',
    background: '#fafafa', borderBottom: '1px solid #eee',
  };
  const tdStyle: React.CSSProperties = {
    padding: '12px 14px', fontSize: 14, color: '#333',
    borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle',
  };

  return (
    <div style={{ padding: 32, maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Benutzerverwaltung</h2>
          <p style={{ margin: '4px 0 0', color: '#888', fontSize: 14 }}>
            Mitarbeiter-Zugänge für {me?.tenant_name}
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          style={{
            padding: '9px 18px', background: ACCENT, color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 14,
            fontWeight: 600, cursor: 'pointer',
          }}
        >
          + Benutzer anlegen
        </button>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #eee', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>Wird geladen...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>E-Mail</th>
                <th style={thStyle}>Rolle</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Letzter Login</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.5 }}>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 32, height: 32,
                        background: u.role === 'admin' ? ACCENT : '#6366f1',
                        borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0,
                      }}>
                        {u.full_name.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 500 }}>
                        {u.full_name}
                        {u.id === me?.id && (
                          <span style={{ marginLeft: 6, fontSize: 11, color: '#888', fontWeight: 400 }}>(ich)</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td style={{ ...tdStyle, color: '#555' }}>{u.email}</td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '3px 9px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      background: u.role === 'admin' ? '#fef2f2' : '#f0f0ff',
                      color:      u.role === 'admin' ? ACCENT     : '#6366f1',
                    }}>
                      {u.role === 'admin' ? 'Admin' : 'Benutzer'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '3px 9px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      background: u.is_active ? '#f0fdf4' : '#fafafa',
                      color:      u.is_active ? '#16a34a' : '#888',
                    }}>
                      {u.is_active ? 'Aktiv' : 'Deaktiviert'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, color: '#888', fontSize: 13 }}>
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setEditTarget(u)}
                        style={{ padding: '5px 10px', fontSize: 12, border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => setResetTarget(u)}
                        style={{ padding: '5px 10px', fontSize: 12, border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
                      >
                        Passwort
                      </button>
                      {u.id !== me?.id && (
                        <button
                          onClick={() => handleDelete(u)}
                          style={{ padding: '5px 10px', fontSize: 12, border: '1px solid #fecaca', borderRadius: 6, background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}
                        >
                          Löschen
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#888', padding: 32 }}>Keine Benutzer gefunden.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showAdd    && <AddUserModal onClose={() => setShowAdd(false)} onSaved={load} onError={setError} />}
      {editTarget && <EditUserModal user={editTarget} onClose={() => setEditTarget(null)} onSaved={load} onError={setError} />}
      {resetTarget && <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} onError={setError} />}
    </div>
  );

  async function handleDelete(u: CrmUser) {
    if (!confirm(`Benutzer "${u.full_name}" wirklich löschen?`)) return;
    try {
      await deleteUser(u.id);
      await load();
    } catch {
      setError('Löschen fehlgeschlagen.');
    }
  }
}

// ── Modal: Benutzer anlegen ────────────────────────────────────────────
function AddUserModal({ onClose, onSaved, onError }: {
  onClose: () => void; onSaved: () => void; onError: (e: string) => void;
}) {
  const [form, setForm] = useState({ email: '', full_name: '', role: 'user' as 'admin' | 'user', password: '', password2: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (form.password !== form.password2) { setErr('Passwörter stimmen nicht überein.'); return; }
    if (form.password.length < 8) { setErr('Mindestens 8 Zeichen.'); return; }
    setSaving(true);
    try {
      await createUser({ email: form.email, full_name: form.full_name, role: form.role, password: form.password });
      onSaved(); onClose();
    } catch (e: unknown) {
      setErr((e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Fehler beim Anlegen.');
    } finally { setSaving(false); }
  }

  return <Modal title="Benutzer anlegen" onClose={onClose}>
    <form onSubmit={handleSubmit}>
      {err && <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</div>}
      <FormRow label="Name"><input required value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} style={inputStyle} /></FormRow>
      <FormRow label="E-Mail"><input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} /></FormRow>
      <FormRow label="Rolle">
        <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value as 'admin' | 'user' }))} style={inputStyle}>
          <option value="user">Benutzer</option>
          <option value="admin">Admin</option>
        </select>
      </FormRow>
      <FormRow label="Passwort"><input type="password" required value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min. 8 Zeichen" style={inputStyle} /></FormRow>
      <FormRow label="Bestätigen"><input type="password" required value={form.password2} onChange={e => setForm(f => ({ ...f, password2: e.target.value }))} style={inputStyle} /></FormRow>
      <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} style={cancelBtn}>Abbrechen</button>
        <button type="submit" disabled={saving} style={saveBtn}>{saving ? 'Speichern...' : 'Anlegen'}</button>
      </div>
    </form>
  </Modal>;
}

// ── Modal: Benutzer bearbeiten ─────────────────────────────────────────
function EditUserModal({ user, onClose, onSaved, onError }: {
  user: CrmUser; onClose: () => void; onSaved: () => void; onError: (e: string) => void;
}) {
  const [form, setForm] = useState({ full_name: user.full_name, role: user.role, is_active: user.is_active });
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await updateUser(user.id, form);
      onSaved(); onClose();
    } catch { onError('Fehler beim Speichern.'); }
    finally { setSaving(false); }
  }

  return <Modal title="Benutzer bearbeiten" onClose={onClose}>
    <form onSubmit={handleSubmit}>
      <FormRow label="Name"><input required value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} style={inputStyle} /></FormRow>
      <FormRow label="Rolle">
        <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value as 'admin' | 'user' }))} style={inputStyle}>
          <option value="user">Benutzer</option>
          <option value="admin">Admin</option>
        </select>
      </FormRow>
      <FormRow label="Status">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
          <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
          Aktiv
        </label>
      </FormRow>
      <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} style={cancelBtn}>Abbrechen</button>
        <button type="submit" disabled={saving} style={saveBtn}>{saving ? 'Speichern...' : 'Speichern'}</button>
      </div>
    </form>
  </Modal>;
}

// ── Modal: Passwort zurücksetzen ───────────────────────────────────────
function ResetPasswordModal({ user, onClose, onError }: {
  user: CrmUser; onClose: () => void; onError: (e: string) => void;
}) {
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (pw1 !== pw2) { setErr('Passwörter stimmen nicht überein.'); return; }
    if (pw1.length < 8) { setErr('Mindestens 8 Zeichen.'); return; }
    setSaving(true);
    try {
      await resetUserPassword(user.id, pw1);
      onClose();
    } catch { onError('Fehler beim Zurücksetzen.'); }
    finally { setSaving(false); }
  }

  return <Modal title={`Passwort für ${user.full_name}`} onClose={onClose}>
    <form onSubmit={handleSubmit}>
      {err && <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</div>}
      <FormRow label="Neues Passwort"><input type="password" required value={pw1} onChange={e => setPw1(e.target.value)} placeholder="Min. 8 Zeichen" style={inputStyle} /></FormRow>
      <FormRow label="Bestätigen"><input type="password" required value={pw2} onChange={e => setPw2(e.target.value)} style={inputStyle} /></FormRow>
      <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} style={cancelBtn}>Abbrechen</button>
        <button type="submit" disabled={saving} style={saveBtn}>{saving ? 'Speichern...' : 'Zurücksetzen'}</button>
      </div>
    </form>
  </Modal>;
}

// ── Shared Components ──────────────────────────────────────────────────
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 440, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#666', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  padding: '9px 11px', border: '1px solid #ddd',
  borderRadius: 7, fontSize: 14, outline: 'none',
};
const saveBtn: React.CSSProperties = {
  padding: '9px 18px', background: ACCENT, color: '#fff',
  border: 'none', borderRadius: 7, fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const cancelBtn: React.CSSProperties = {
  padding: '9px 18px', background: '#fff', color: '#555',
  border: '1px solid #ddd', borderRadius: 7, fontSize: 14, cursor: 'pointer',
};
